import { toast } from 'react-toastify';
import { useDebounceValue } from 'usehooks-ts';
import React, { useMemo, useRef, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  createDataCatalog,
  IDataCatalog,
  IDataCatalogEntryFields,
  saveDataCatalogEntries,
  TDataCatalogEntryType,
  VerdocsEndpoint,
} from '@verdocs/js-sdk';
import { useDataCatalogListing, useDataCatalogMarketplace } from '../react-sdk/queries';
import { Button, FIELD_CLASSES, Spinner } from '../Components';
import { PublisherTile } from './DataCatalogMarketplaceDialog';
import { AppState } from '../AppState';
import { Dialog } from './Dialog';

type TSource = 'json_sample' | 'json_schema' | 'csv_header' | 'marketplace';
type TPasteSource = Exclude<TSource, 'marketplace'>;

const SOURCES: { id: TSource; label: string }[] = [
  { id: 'json_sample', label: 'Sample JSON' },
  { id: 'json_schema', label: 'JSON Schema' },
  { id: 'csv_header', label: 'CSV header row' },
  { id: 'marketplace', label: 'Marketplace' },
];

const SAMPLES: Record<TPasteSource, string> = {
  json_sample: JSON.stringify(
    {
      policy_id: '1234567890',
      applicant: {
        full_name: 'Jordan Rivera',
        date_of_birth: '1988-04-12',
        email: 'jordan.rivera@example.com',
        phone: '(555) 010-0199',
        mailing_address: { line1: '100 Example Ave', city: 'Springfield', state: 'OH', postal_code: '45402' },
      },
      co_applicant: { full_name: 'Casey Rivera' },
      property: { address: { line1: '48 Harbor Rd', city: 'Springfield', state: 'OH', postal_code: '45404' }, year_built: 1994 },
      policy: {
        number: 'HO-2026-001234',
        effective_date: '2026-11-01',
        annual_premium: 1840,
        coverage: { level: 'standard', dwelling_limit: 350000, flood: true },
      },
      agent: { full_name: 'Sam Lee', license_number: 'OH-1234567' },
    },
    null,
    2,
  ),
  json_schema: JSON.stringify(
    {
      type: 'object',
      properties: {
        applicant: {
          type: 'object',
          properties: {
            full_name: { type: 'string', title: 'Applicant name' },
            date_of_birth: { type: 'string', format: 'date', title: 'Date of birth' },
            email: { type: 'string', title: 'Email' },
          },
        },
        policy: {
          type: 'object',
          properties: {
            number: { type: 'string', title: 'Policy number' },
            effective_date: { type: 'string', format: 'date', title: 'Effective date' },
            coverage_level: { type: 'string', title: 'Coverage level', enum: [ 'basic', 'standard', 'premium' ] },
            flood: { type: 'boolean', title: 'Flood coverage' },
          },
        },
      },
    },
    null,
    2,
  ),
  csv_header: 'applicant.full_name,applicant.email,applicant.phone,policy.number,policy.effective_date,agent.full_name',
};

const TYPE_LABELS: Record<TDataCatalogEntryType, string> = { text: 'Text', date: 'Date', boolean: 'Yes/No', choice: 'Choice' };

const DATA_KEY_PATTERN = /^[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]+)*$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T.*)?$/;

const humanize = (segment: string) => {
  const words = segment
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

interface IFoundKeys {
  entries: IDataCatalogEntryFields[];
  invalid: string[];
  lists: string[];
}

// The api parses imports the same way, so these match what an import through the api creates
const parseKeys = (source: TPasteSource, text: string, keepExamples: boolean): IFoundKeys | { error: string } => {
  const found: IFoundKeys = { entries: [], invalid: [], lists: [] };
  const add = (key: string, entry: Omit<IDataCatalogEntryFields, 'key' | 'example'>, example?: unknown) => {
    if (!DATA_KEY_PATTERN.test(key) || key.length > 255) {
      found.invalid.push(key);
    } else {
      found.entries.push({ ...entry, key, example: keepExamples && example !== undefined && example !== null ? String(example) : null });
    }
  };

  if (source === 'csv_header') {
    const [ header = '' ] = text.split(/\r?\n/);
    header
      .split(',')
      .map(column => column.trim().replace(/^"|"$/g, '').trim())
      .filter(Boolean)
      .forEach(column => {
        const key = column.toLowerCase().replace(/[^a-z0-9_.-]+/g, '_');
        add(key, { label: humanize(key.split('.').pop()!), type: 'text', description: null, options: null, sensitive: false });
      });
    return found.entries.length || found.invalid.length ? found : { error: 'Paste a header row, such as applicant.full_name,policy.number.' };
  }

  let content: unknown;
  try {
    content = JSON.parse(text);
  } catch {
    return { error: "That isn't valid JSON yet." };
  }

  if (!content || typeof content !== 'object' || Array.isArray(content)) {
    return { error: 'Paste a JSON object.' };
  }

  const walkSample = (value: Record<string, any>, prefix: string) =>
    Object.entries(value).forEach(([ name, child ]) => {
      const key = prefix ? `${prefix}.${name}` : name;
      if (Array.isArray(child)) {
        found.lists.push(key);
      } else if (child && typeof child === 'object') {
        walkSample(child, key);
      } else {
        const type = typeof child === 'boolean' ? 'boolean' : typeof child === 'string' && ISO_DATE.test(child) ? 'date' : 'text';
        add(key, { label: humanize(name), type, description: null, options: null, sensitive: false }, child);
      }
    });

  const walkSchema = (schema: Record<string, any>, prefix: string) =>
    Object.entries((schema.properties as Record<string, any>) || {}).forEach(([ name, property ]) => {
      const key = prefix ? `${prefix}.${name}` : name;
      if (property?.type === 'array') {
        found.lists.push(key);
      } else if (property?.type === 'object' || property?.properties) {
        walkSchema(property, key);
      } else {
        const options = Array.isArray(property?.enum) ? property.enum.map((value: unknown) => ({ value: String(value), label: String(value) })) : null;
        const type = options ? 'choice' : property?.type === 'boolean' ? 'boolean' : [ 'date', 'date-time' ].includes(property?.format) ? 'date' : 'text';
        const example = Array.isArray(property?.examples) ? property.examples[0] : property?.example;
        add(key, { label: property?.title || humanize(name), type, description: property?.description ?? null, options, sensitive: false }, example);
      }
    });

  if (source === 'json_schema') {
    walkSchema(content as Record<string, any>, '');
  } else {
    walkSample(content as Record<string, any>, '');
  }

  return found.entries.length || found.invalid.length || found.lists.length ? found : { error: 'Nothing here becomes a key.' };
};

const TOKEN = /("(?:[^"\\]|\\.)*")(\s*:)?|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|\b(true|false|null)\b/g;

// Colors keys, strings, numbers, and literals for the editor's backdrop
const highlightJSON = (text: string) => {
  const parts: React.ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(TOKEN)) {
    parts.push(text.slice(last, match.index));
    const [ whole, string, colon, number, literal ] = match;
    const color = string ? (colon ? 'text-[#4b3fa0]' : 'text-[#2e7d4f]') : number ? 'text-[#c2570c]' : 'text-[#9333a8]';
    parts.push(
      <span
        key={match.index}
        className={color}>
        {string || number || literal}
      </span>,
    );
    if (colon) parts.push(colon);
    last = match.index! + whole.length;
  }

  parts.push(text.slice(last));
  return parts;
};

function CodeEditor({ value, onChange, highlight }: { value: string; onChange: (value: string) => void; highlight: boolean }) {
  const backdrop = useRef<HTMLPreElement>(null);
  const shared = 'm-0 whitespace-pre p-3 font-mono text-[12px] leading-[1.6]';

  return (
    <div className="relative min-h-0 flex-1 overflow-hidden rounded-md border border-[#dcdee5] bg-[#fcfcfd] focus-within:border-[#654dcb]">
      <pre
        ref={backdrop}
        aria-hidden
        className={`${shared} pointer-events-none absolute inset-0 overflow-hidden text-[#33364b]`}>
        {highlight ? highlightJSON(value) : value}
        {'\n'}
      </pre>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        onScroll={e => backdrop.current?.scrollTo(e.currentTarget.scrollLeft, e.currentTarget.scrollTop)}
        spellCheck={false}
        className={`${shared} relative h-full w-full resize-none bg-transparent text-transparent caret-[#33364b] outline-none selection:bg-[#d9d2fb]`}
      />
    </div>
  );
}

interface ImportDataCatalogDialogProps {
  /** Keys go into this catalog. Without one, the dialog creates a new catalog. */
  catalog?: IDataCatalog;
  onClose: () => void;
  onDone: (catalogId: string) => void;
}

export function ImportDataCatalogDialog({ catalog, onClose, onDone }: ImportDataCatalogDialogProps) {
  const organizationId = AppState.currentProfile?.organization_id || '';
  const [ step, setStep ] = useState<1 | 2>(1);
  const [ name, setName ] = useState('');
  const [ source, setSource ] = useState<TSource>('json_sample');
  const [ texts, setTexts ] = useState<Record<TPasteSource, string>>(SAMPLES);
  const [ keepExamples, setKeepExamples ] = useState(true);
  const [ error, setError ] = useState('');
  const [ found, setFound ] = useState<IFoundKeys | null>(null);
  const [ selected, setSelected ] = useState<Record<string, boolean>>({});
  const [ labels, setLabels ] = useState<Record<string, string>>({});
  const [ types, setTypes ] = useState<Record<string, TDataCatalogEntryType>>({});
  const [ showExisting, setShowExisting ] = useState(false);
  const [ submitting, setSubmitting ] = useState(false);

  const [ search, setSearch ] = useState('');
  const [ q, setQ ] = useDebounceValue('', 250);
  const [ listingId, setListingId ] = useState('');
  const marketplaceQuery = useDataCatalogMarketplace(source === 'marketplace' ? organizationId : '', q);
  const listings = marketplaceQuery.data || [];
  const listingQuery = useDataCatalogListing(organizationId, listingId);
  const listing = listingQuery.data?.id === listingId ? listingQuery.data : undefined;

  const existingKeys = useMemo(() => new Set((catalog?.entries || []).map(entry => entry.key)), [ catalog ]);
  const fresh = (found?.entries || []).filter(entry => !existingKeys.has(entry.key));
  const existing = (found?.entries || []).filter(entry => existingKeys.has(entry.key));
  const picked = fresh.filter(entry => selected[entry.key] !== false);
  const allPicked = fresh.length > 0 && picked.length === fresh.length;

  const canFind = source === 'marketplace' ? !!listing : !!texts[source].trim();

  const handleFindKeys = () => {
    if (source === 'marketplace') {
      if (!listing) return;
      setFound({ entries: listing.entries.map(entry => ({ ...entry })), invalid: [], lists: [] });
    } else {
      const result = parseKeys(source, texts[source], keepExamples);
      if ('error' in result) {
        setError(result.error);
        return;
      }

      setFound(result);
    }

    setError('');
    setSelected({});
    setLabels({});
    setTypes({});
    setStep(2);
  };

  const handleSubmit = async () => {
    const entries = picked.map(entry => {
      const type = types[entry.key] || entry.type;
      return { ...entry, label: (labels[entry.key] ?? entry.label).trim() || entry.label, type, options: type === 'choice' ? entry.options : null };
    });
    const endpoint = VerdocsEndpoint.getDefault();
    const plural = `${entries.length} ${entries.length === 1 ? 'key' : 'keys'}`;

    setSubmitting(true);
    if (catalog) {
      try {
        const saved = await saveDataCatalogEntries(endpoint, organizationId, catalog.id, entries);
        toast.success(saved.some(entry => entry.status === 'proposed') ? `Suggested ${plural} for an admin to approve` : `Added ${plural}`);
        onDone(catalog.id);
      } catch (e: any) {
        setSubmitting(false);
        toast.error(`Error adding the keys: ${e.response?.data?.message || e.message}`);
      }
      return;
    }

    try {
      const created = await createDataCatalog(endpoint, organizationId, { name: name.trim(), ...(entries.length ? { entries } : {}) });
      toast.success(`Created ${created.name} with ${plural}`);
      onDone(created.id);
    } catch (e: any) {
      setSubmitting(false);
      toast.error(`Error creating the catalog: ${e.response?.data?.message || e.message}`);
    }
  };

  const header = (
    <div className="-mx-5 -mt-4 mb-4 flex items-center gap-2.5 border-b border-[#ebecf0] px-5 pb-3 pt-4">
      <div className="text-lg font-medium text-[#33364b]">{catalog ? 'Import keys' : 'Import a new catalog'}</div>
      <span className="text-[12px] text-gray-400">Step {step} of 2</span>
      <span className="flex-1" />
      <button
        onClick={onClose}
        className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        aria-label="Close">
        <XMarkIcon className="size-5" />
      </button>
    </div>
  );

  if (step === 1) {
    return (
      <Dialog
        onCancel={onClose}
        size="lg">
        {header}

        {!catalog && (
          <div className="mb-4 w-[360px]">
            <label className="mb-1 block text-[13px] font-medium text-[#33364b]">
              Name<span className="ml-0.5 text-[#c2410c]">*</span>
            </label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Carrier application"
              className={FIELD_CLASSES}
            />
          </div>
        )}

        <div className="mb-3 text-[13px] text-gray-500">
          {source === 'marketplace' ? 'Copy keys from a catalog shared with your organization.' : 'Each path in what you paste becomes a key.'}
        </div>

        <div className="mb-3 flex gap-5 border-b border-[#ebecf0]">
          {SOURCES.map(option => (
            <button
              key={option.id}
              onClick={() => {
                setSource(option.id);
                setError('');
              }}
              className={`-mb-px border-b-2 pb-2 text-[13px] font-medium ${
                source === option.id ? 'border-[#654dcb] text-[#654dcb]' : 'border-transparent text-gray-500 hover:text-[#33364b]'
              }`}>
              {option.label}
            </button>
          ))}
        </div>

        {source === 'marketplace' ? (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="relative mb-2.5">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={e => {
                  setSearch(e.target.value);
                  setQ(e.target.value.trim());
                }}
                placeholder="Search by name, publisher, or key"
                className="h-9 w-full rounded-md border border-[#dcdee5] pl-9 pr-3 text-[13px] text-[#33364b] outline-none placeholder:text-gray-400 focus:border-[#654dcb]"
              />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-[#ebecf0] p-1.5">
              {marketplaceQuery.isLoading ? (
                <div className="flex justify-center py-10">
                  <Spinner mode="dark" />
                </div>
              ) : listings.length === 0 ? (
                <div className="px-5 py-10 text-center text-[13px] text-gray-500">
                  <div className="mb-1 text-[14px] font-medium text-[#33364b]">{q ? `No catalogs match "${q}"` : 'Nothing to import yet'}</div>
                  {q ? 'Try a publisher’s name or a key such as applicant.full_name.' : 'Catalogs appear here once an organization above yours shares one, or once one is approved for the public marketplace.'}
                </div>
              ) : (
                listings.map(candidate => (
                  <button
                    key={candidate.id}
                    onClick={() => {
                      // A new catalog takes the listing's name until someone types their own
                      if (!catalog && (!name.trim() || name === listing?.name)) setName(candidate.name);
                      setListingId(candidate.id);
                    }}
                    className={`flex w-full gap-3 rounded-lg border px-2.5 py-2.5 text-left ${
                      candidate.id === listingId ? 'border-[#d9d2fb] bg-[#f8f7fe]' : 'border-transparent hover:bg-[#fafafb]'
                    }`}>
                    <PublisherTile listing={candidate} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[14px] font-medium text-[#33364b]">{candidate.name}</div>
                      <div className="mt-px text-[12px] text-gray-500">
                        {candidate.publisher.name} · {candidate.key_count} keys · {candidate.listing === 'public' ? 'Public' : 'Shared with you'}
                      </div>
                      {candidate.description && <div className="mt-1 line-clamp-2 text-[12px] leading-snug text-gray-500">{candidate.description}</div>}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        ) : (
          <CodeEditor
            value={texts[source]}
            onChange={text => setTexts({ ...texts, [source]: text })}
            highlight={source !== 'csv_header'}
          />
        )}

        {error && <div className="mt-2 text-[12.5px] text-[#c2410c]">{error}</div>}

        {(source === 'json_sample' || source === 'json_schema') && (
          <label className="mt-3.5 flex cursor-pointer items-start gap-2.5">
            <input
              type="checkbox"
              checked={keepExamples}
              onChange={e => setKeepExamples(e.target.checked)}
              className="mt-0.5 size-4 shrink-0 rounded border-[#dcdee5] accent-[#654dcb]"
            />
            <span>
              <span className="block text-[13px] font-medium text-[#33364b]">Keep these values as examples</span>
              <span className="block text-[12px] text-gray-500">Examples show in template previews, where template owners can see them.</span>
            </span>
          </label>
        )}

        <div className="mt-4 flex items-center justify-end gap-2 border-t border-[#f2f3f5] pt-4">
          <Button
            type="button"
            size="small"
            variant="secondary"
            label="Cancel"
            onClick={onClose}
          />
          <Button
            size="small"
            label="Find keys"
            onClick={handleFindKeys}
            disabled={!canFind || (!catalog && !name.trim())}
          />
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      onCancel={onClose}
      size="lg">
      {header}

      <div className="mb-3 text-[13px] text-[#33364b]">
        <span className="font-medium">
          Found {fresh.length + existing.length} {fresh.length + existing.length === 1 ? 'key' : 'keys'}.
        </span>{' '}
        {catalog && `${existing.length} ${existing.length === 1 ? 'is' : 'are'} already in ${catalog.name}.`}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-[#ebecf0]">
        <table className="w-full table-fixed text-[12.5px]">
          <colgroup>
            <col className="w-[38px]" />
            <col className="w-[260px]" />
            <col />
            <col className="w-[110px]" />
            <col className="w-[150px]" />
            <col className="w-[56px]" />
          </colgroup>
          <thead className="sticky top-0 z-10 border-b border-[#ebecf0] bg-[#fafafb]">
            <tr className="text-left text-[11.5px] text-gray-500">
              <th className="px-3 py-2">
                <input
                  type="checkbox"
                  aria-label="Select all"
                  checked={allPicked}
                  disabled={!fresh.length}
                  onChange={e => setSelected(Object.fromEntries(fresh.map(entry => [ entry.key, e.target.checked ])))}
                  className="size-4 accent-[#654dcb]"
                />
              </th>
              <th className="px-2 py-2 font-medium">Key</th>
              <th className="px-2 py-2 font-medium">Label</th>
              <th className="px-2 py-2 font-medium">Type</th>
              <th className="px-2 py-2 font-medium">Example</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {fresh.map(entry => {
              const on = selected[entry.key] !== false;
              const type = types[entry.key] || entry.type;
              return (
                <tr
                  key={entry.key}
                  className={`border-t border-[#f2f3f5] first:border-0 ${on ? '' : 'opacity-50'}`}>
                  <td className="px-3 py-1.5">
                    <input
                      type="checkbox"
                      aria-label={`Import ${entry.key}`}
                      checked={on}
                      onChange={e => setSelected({ ...selected, [entry.key]: e.target.checked })}
                      className="size-4 accent-[#654dcb]"
                    />
                  </td>
                  <td
                    className="truncate px-2 py-1.5 font-mono text-[12px] text-[#4b3fa0]"
                    title={entry.key}>
                    {entry.key}
                  </td>
                  <td className="px-2 py-1">
                    <input
                      value={labels[entry.key] ?? entry.label}
                      onChange={e => setLabels({ ...labels, [entry.key]: e.target.value })}
                      className="h-7 w-full rounded border border-[#dcdee5] px-2 text-[12.5px] text-[#33364b] outline-none focus:border-[#654dcb]"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <select
                      value={type}
                      onChange={e => setTypes({ ...types, [entry.key]: e.target.value as TDataCatalogEntryType })}
                      className="h-7 w-full rounded border border-[#dcdee5] bg-white px-1.5 text-[12.5px] text-[#33364b] outline-none focus:border-[#654dcb]">
                      {(Object.keys(TYPE_LABELS) as TDataCatalogEntryType[])
                        .filter(option => option !== 'choice' || entry.options?.length)
                        .map(option => (
                          <option
                            key={option}
                            value={option}>
                            {TYPE_LABELS[option]}
                          </option>
                        ))}
                    </select>
                  </td>
                  <td className="truncate px-2 py-1.5 text-gray-500">
                    {type === 'choice' ? (entry.options || []).map(option => option.label).join(', ') : entry.example}
                  </td>
                  <td className="px-2 py-1.5">
                    <span className="rounded bg-[#e3f4ea] px-1.5 py-px text-[11px] font-medium text-[#2e9b64]">New</span>
                  </td>
                </tr>
              );
            })}

            {existing.length > 0 && (
              <>
                <tr
                  className="cursor-pointer border-t border-[#f2f3f5] bg-[#fbfbfc]"
                  onClick={() => setShowExisting(!showExisting)}>
                  <td />
                  <td
                    colSpan={5}
                    className="px-2 py-2 text-[12.5px] text-gray-500">
                    <span className="inline-flex items-center gap-1">
                      {showExisting ? <ChevronDownIcon className="size-3.5" /> : <ChevronRightIcon className="size-3.5" />}
                      {existing.length} already in {catalog?.name}, no changes
                    </span>
                  </td>
                </tr>
                {showExisting && (
                  <tr className="bg-[#fbfbfc]">
                    <td />
                    <td
                      colSpan={5}
                      className="px-2 pb-2.5">
                      <div className="flex flex-wrap gap-x-4 gap-y-1">
                        {existing.map(entry => (
                          <span
                            key={entry.key}
                            className="font-mono text-[12px] text-gray-500">
                            {entry.key}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
        {fresh.length === 0 && existing.length === 0 && <div className="p-6 text-center text-[13px] text-gray-500">No keys found.</div>}
      </div>

      {(found?.invalid.length || found?.lists.length) ? (
        <div className="mt-2 flex flex-col gap-0.5 text-[12px] text-gray-500">
          {!!found?.invalid.length && <div>Skipped names that can&apos;t be data keys: {found.invalid.join(', ')}</div>}
          {!!found?.lists.length && <div>Skipped lists: {found.lists.join(', ')}</div>}
        </div>
      ) : null}

      <div className="mt-4 flex items-center gap-2 border-t border-[#f2f3f5] pt-4">
        <Button
          type="button"
          size="small"
          variant="secondary"
          label="Back"
          onClick={() => setStep(1)}
          disabled={submitting}
        />
        <span className="flex-1" />
        {submitting && <Spinner mode="dark" />}
        <Button
          size="small"
          label={catalog ? `Add ${picked.length} ${picked.length === 1 ? 'key' : 'keys'}` : `Create with ${picked.length} ${picked.length === 1 ? 'key' : 'keys'}`}
          onClick={handleSubmit}
          disabled={submitting || (catalog ? !picked.length : !name.trim())}
        />
      </div>
    </Dialog>
  );
}
